import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import SharedCatalogueChrome from "./SharedCatalogueChrome";
import { CatalogueVisitorProvider, useCatalogueVisitor } from "./CatalogueVisitorContext";
import {
  clearVisitorShelfDraft,
  fetchVisitorShelves,
  removeVisitorShelfCartLine,
  removeVisitorShelfShortlistLine,
  upsertVisitorShelfCartLine,
} from "./catalogueVisitorClient";
import {
  submitVisitorShelfToOrganisation,
  type VisitorSubmitMode,
} from "./catalogueCommerceClient";
import {
  appendVisitorOrder,
  loadVisitorOrders,
  type VisitorOrderRecord,
} from "./visitorSubmittedOrders";
import { VisitorSubmittedOrdersSection } from "./VisitorSubmittedOrdersSection";
import { getLastVisitedCatalogue } from "./catalogueVisitorSession";
import notifications from "../../../common/Components/Notifications/Notification";
import { parseOptionalGst } from "./catalogueVisitorGst";
import "./sharedCatalogue.css";

type ShelfCartLine = {
  productKey?: string;
  productId?: string;
  productUniqueId?: string;
  productName?: string;
  productColor?: string;
  productImage?: string;
  designNum?: string;
  quantity?: number;
  sellPriceMrpSnapshot?: string;
};

type ShelfShortlistLine = {
  productKey?: string;
  productId?: string;
  productUniqueId?: string;
  productName?: string;
  productColor?: string;
  productImage?: string;
  designNum?: string;
};

type ShelfDoc = {
  catalogueId?: string;
  cartLines?: ShelfCartLine[];
  shortlistLines?: ShelfShortlistLine[];
};

function parsePriceSnapshot(raw?: string): number {
  if (raw == null || raw === "") return 0;
  const n = parseFloat(String(raw).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatInr(n: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function clampQty(n: number): number {
  return Math.min(999, Math.max(1, Math.trunc(n)));
}

function computeTotals(shelves: ShelfDoc[]) {
  let pieceCount = 0;
  let lineCount = 0;
  let subtotal = 0;
  for (const s of shelves) {
    const cart = Array.isArray(s.cartLines) ? s.cartLines : [];
    const sl = Array.isArray(s.shortlistLines) ? s.shortlistLines : [];
    for (const line of cart) {
      const q = clampQty(Number(line.quantity) || 1);
      pieceCount += q;
      lineCount += 1;
      subtotal += parsePriceSnapshot(line.sellPriceMrpSnapshot) * q;
    }
    for (const _ of sl) {
      pieceCount += 1;
      lineCount += 1;
    }
  }
  return { pieceCount, lineCount, subtotal };
}

const PublicCatalogueVisitorAccountInner: React.FC = () => {
  const history = useHistory();
  const { isVisitorSession, profile, signOutVisitor, openAuthModal } = useCatalogueVisitor();
  const [shelves, setShelves] = useState<ShelfDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState<Record<string, boolean>>({});
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitGst, setSubmitGst] = useState("");
  const [submitAddress, setSubmitAddress] = useState("");
  const [orderLog, setOrderLog] = useState<VisitorOrderRecord[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});
  // Collection names for the shelf headings (shelves store only the id).
  const [collectionNames, setCollectionNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!isVisitorSession) {
      setShelves([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const json = await fetchVisitorShelves();
      if (json?.success && json.data != null) {
        const list = Array.isArray(json.data) ? (json.data as ShelfDoc[]) : [];
        setShelves(list);
      } else {
        setShelves([]);
        setError(json?.message || "Could not load your selections.");
      }
    } catch {
      setShelves([]);
      setError("Could not load your selections.");
    } finally {
      setLoading(false);
    }
  }, [isVisitorSession]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (profile?.visitorId) {
      setOrderLog(loadVisitorOrders(profile.visitorId));
    } else {
      setOrderLog([]);
    }
  }, [profile?.visitorId]);

  const continuePath = useMemo(() => {
    const last = getLastVisitedCatalogue();
    if (last?.catalogueId) {
      const q = last.search ? `?${last.search}` : "";
      return `/catalogue-products/${encodeURIComponent(last.catalogueId)}${q}`;
    }
    const first = shelves.find(
      (s) => (s.cartLines?.length || 0) > 0 || (s.shortlistLines?.length || 0) > 0
    );
    if (first?.catalogueId) {
      return `/catalogue-products/${encodeURIComponent(first.catalogueId)}`;
    }
    return null;
  }, [shelves]);

  const totals = useMemo(() => computeTotals(shelves), [shelves]);

  const nonEmptyShelves = useMemo(
    () =>
      shelves.filter(
        (s) => (s.cartLines?.length || 0) > 0 || (s.shortlistLines?.length || 0) > 0
      ),
    [shelves]
  );

  useEffect(() => {
    const base = process.env.REACT_APP_SERVERURL || "";
    const wanted = shelves
      .map((s) => ({
        id: s.catalogueId || "",
        link: [...(s.cartLines || []), ...(s.shortlistLines || [])]
          .map((l: any) => l?.shareLinkToken)
          .find((t: any) => typeof t === "string" && t.trim()),
      }))
      .filter((w) => w.id && w.link && !collectionNames[w.id]);
    if (!wanted.length) return;
    let cancelled = false;
    Promise.all(
      wanted.map(async (w) => {
        try {
          const res = await fetch(
            `${base}/catalogue-security/${encodeURIComponent(w.id)}/public-status?link=${encodeURIComponent(w.link)}`
          );
          const json = await res.json();
          return [w.id, String(json?.data?.catalogueName || "").trim()] as const;
        } catch {
          return [w.id, ""] as const;
        }
      })
    ).then((pairs) => {
      if (cancelled) return;
      setCollectionNames((prev) => {
        const next = { ...prev };
        for (const [id, name] of pairs) if (name) next[id] = name;
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelves]);

  const anyMutating = useMemo(() => Object.values(mutating).some(Boolean), [mutating]);

  const setBusy = (key: string, v: boolean) => {
    setMutating((m) => ({ ...m, [key]: v }));
  };

  const handleBack = () => {
    if (continuePath) {
      history.push(continuePath);
      return;
    }
    history.goBack();
  };

  const handleContinueExplore = () => {
    if (continuePath) history.push(continuePath);
    else {
      notifications({
        title: "Catalogue",
        message: "Open a shared catalogue link first, then your selections will remember that collection.",
        type: "warning",
      });
    }
  };

  const onSubmitShelf = async (catalogueId: string, mode: VisitorSubmitMode) => {
    if (!isVisitorSession || !profile?.visitorId || submitBusy || anyMutating) return;
    const shelf = shelves.find((s) => s.catalogueId === catalogueId);
    if (!shelf) return;
    const cart = Array.isArray(shelf.cartLines) ? shelf.cartLines : [];
    const sl = Array.isArray(shelf.shortlistLines) ? shelf.shortlistLines : [];
    if (mode === "cart" && cart.length === 0) return;
    if (mode === "shortlist" && sl.length === 0) return;
    if (mode === "both" && cart.length === 0 && sl.length === 0) return;

    const gstParsed = parseOptionalGst(submitGst);
    if (!gstParsed.ok) {
      notifications({
        title: "GST",
        message: gstParsed.message,
        type: "warning",
      });
      return;
    }

    setSubmitBusy(true);
    try {
      const result = await submitVisitorShelfToOrganisation(
        {
          catalogueId,
          cartLines: mode === "shortlist" ? [] : cart,
          shortlistLines: mode === "cart" ? [] : sl,
        },
        {
          clientMessage:
            "Final selection submitted from my visitor account — please use the cart and shortlist as sent.",
          mode,
          clientGst: gstParsed.value,
          clientAddress: submitAddress.trim() || undefined,
        }
      );
      if (!result.ok) {
        notifications({
          title: "Could not send",
          message: result.message || "Try again in a moment.",
          type: "danger",
        });
        return;
      }

      const clearCart = mode === "both" || mode === "cart";
      const clearShortlist = mode === "both" || mode === "shortlist";
      const clearRes = await clearVisitorShelfDraft(catalogueId, { clearCart, clearShortlist });
      if (!clearRes?.success) {
        notifications({
          title: "Sent, but draft not cleared",
          message: clearRes?.message || "Your request was sent. Try refreshing, or remove items manually.",
          type: "warning",
        });
      }

      const orderCart =
        mode === "shortlist" ? [] : (JSON.parse(JSON.stringify(cart)) as unknown[]);
      const orderSl =
        mode === "cart" ? [] : (JSON.parse(JSON.stringify(sl)) as unknown[]);
      appendVisitorOrder(profile.visitorId, {
        catalogueId,
        mode,
        cartLines: orderCart,
        shortlistLines: orderSl,
        clientName: profile.name?.trim() || undefined,
        clientPhone: profile.phone?.trim() || undefined,
        clientEmail: profile.email?.trim() || undefined,
      });
      setOrderLog(loadVisitorOrders(profile.visitorId));

      await load();

      const who = profile?.name?.trim() || profile?.phone || "your details";
      notifications({
        title: "Sent to studio",
        message: `Sent under ${who}. Your draft for this part is cleared — you can build a new selection.`,
        type: "success",
      });
    } finally {
      setSubmitBusy(false);
    }
  };

  const onCartQty = async (catalogueId: string, line: ShelfCartLine, nextQty: number) => {
    const pk = line.productKey?.trim();
    if (!pk) return;
    const q = clampQty(nextQty);
    const key = `qty-${catalogueId}-${pk}`;
    if (mutating[key]) return;
    setBusy(key, true);
    try {
      const json = await upsertVisitorShelfCartLine(catalogueId, {
        productKey: pk,
        productId: line.productId || pk,
        productUniqueId: line.productUniqueId,
        productName: line.productName || "Product",
        productColor: line.productColor,
        productImage: line.productImage,
        designNum: line.designNum,
        quantity: q,
        sellPriceMrpSnapshot: line.sellPriceMrpSnapshot,
      });
      if (!json?.success) {
        notifications({
          title: "Cart",
          message: json?.message || "Could not update quantity.",
          type: "danger",
        });
        return;
      }
      await load();
    } finally {
      setBusy(key, false);
    }
  };

  const onRemoveCart = async (catalogueId: string, productKey: string) => {
    const pk = productKey.trim();
    if (!pk) return;
    const key = `rmc-${catalogueId}-${pk}`;
    if (mutating[key]) return;
    setBusy(key, true);
    try {
      const json = await removeVisitorShelfCartLine(catalogueId, pk);
      if (!json?.success) {
        notifications({
          title: "Cart",
          message: json?.message || "Could not remove item.",
          type: "danger",
        });
        return;
      }
      await load();
    } finally {
      setBusy(key, false);
    }
  };

  const onRemoveShortlist = async (catalogueId: string, productKey: string) => {
    const pk = productKey.trim();
    if (!pk) return;
    const key = `rms-${catalogueId}-${pk}`;
    if (mutating[key]) return;
    setBusy(key, true);
    try {
      const json = await removeVisitorShelfShortlistLine(catalogueId, pk);
      if (!json?.success) {
        notifications({
          title: "Shortlist",
          message: json?.message || "Could not remove item.",
          type: "danger",
        });
        return;
      }
      await load();
    } finally {
      setBusy(key, false);
    }
  };

  // One header action; navigation lives in the toolbar below so nothing repeats or overlaps the title.
  const headerActions = (
    <>
      {isVisitorSession ? (
        <button type="button" className="hkp-shared-linkish" onClick={() => signOutVisitor()}>
          Sign out
        </button>
      ) : (
        <button type="button" className="hkp-shared-linkish" onClick={() => openAuthModal()}>
          Sign in
        </button>
      )}
    </>
  );

  return (
    <SharedCatalogueChrome
      organizationName="Your selections"
      subtitle={profile?.name ? `Signed in as ${profile.name}` : "Shortlist & cart"}
      pillText={isVisitorSession && profile?.name?.trim() ? "" : "Visitor"}
      headerActions={headerActions}
    >
      <div className="hkp-visitor-account">
        <div className="hkp-visitor-account-toolbar">
          <button type="button" className="hkp-shared-back" onClick={handleBack}>
            {continuePath ? "← Back to collection" : "← Back"}
          </button>
          {isVisitorSession ? (
            <button
              type="button"
              className="hkp-visitor-account-pill"
              onClick={() => history.push("/catalogue-visitor/submitted")}
            >
              Sent to studio{orderLog.length ? ` · ${orderLog.length}` : ""}
            </button>
          ) : null}
        </div>

        {!isVisitorSession ? (
          <EmptyCard
            title="Sign in to see your selections"
            body="Use the phone number you used in a shared collection. Your cart and shortlist are kept there."
            actionLabel="Sign in"
            onAction={() => openAuthModal()}
          />
        ) : loading ? (
          <div className="hkp-shared-muted hkp-visitor-account-empty-msg">Loading your selections…</div>
        ) : error ? (
          <div className="hkp-shared-muted hkp-visitor-account-empty-msg" role="alert">
            {error}{" "}
            <button type="button" className="hkp-shared-linkish" onClick={() => void load()}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {nonEmptyShelves.length === 0 ? (
              <EmptyCard
                title={orderLog.length ? "Nothing new in your cart" : "Nothing saved yet"}
                body={
                  orderLog.length
                    ? "What you already sent to the studio is listed below. Open a collection to add more pieces."
                    : "Pieces you add from a shared collection collect here until you send them to the studio."
                }
                steps={
                  orderLog.length
                    ? undefined
                    : ["Open a shared collection", "Tap “Add to selection” or “Add to cart”", "Come back here and send it to the studio"]
                }
                actionLabel={continuePath ? "Open the collection" : undefined}
                onAction={continuePath ? handleContinueExplore : undefined}
              />
            ) : (
              <>
                <header className="hkp-visitor-account-title-row">
                  <div className="hkp-visitor-account-title-block">
                    <h1 className="hkp-visitor-account-h1">Your selections</h1>
                    <p className="hkp-visitor-account-sub">
                      {totals.lineCount} product{totals.lineCount === 1 ? "" : "s"} · {totals.pieceCount}{" "}
                      piece
                      {totals.pieceCount === 1 ? "" : "s"} · Subtotal {formatInr(totals.subtotal)}
                    </p>
                    <p className="hkp-visitor-account-lead">
                      Pieces stay here as a draft until you send them to the studio.
                    </p>
                  </div>
                </header>


                <div className="hkp-visitor-account-sections">
              {nonEmptyShelves.map((s) => {
                const cid = s.catalogueId || "";
                const cart = Array.isArray(s.cartLines) ? s.cartLines : [];
                const shortlist = Array.isArray(s.shortlistLines) ? s.shortlistLines : [];
                let sectionSubtotal = 0;
                let sectionPieces = 0;
                for (const line of cart) {
                  const q = clampQty(Number(line.quantity) || 1);
                  sectionPieces += q;
                  sectionSubtotal += parsePriceSnapshot(line.sellPriceMrpSnapshot) * q;
                }
                sectionPieces += shortlist.length;

                return (
                  <section key={cid || "unknown"} className="hkp-visitor-account-card">
                    <div className="hkp-visitor-account-card-head">
                      <div>
                        <h2 className="hkp-visitor-account-h2 hkp-visitor-account-collection-name">{collectionNames[cid] || "Your collection"}</h2>
                        <p className="hkp-visitor-account-catalogue-id">
                          {cart.length ? `${cart.length} in cart` : ""}
                          {cart.length && shortlist.length ? " · " : ""}
                          {shortlist.length ? `${shortlist.length} shortlisted` : ""}
                        </p>
                      </div>
                      {cid ? (
                        <button
                          type="button"
                          className="hkp-visitor-account-outline-btn"
                          onClick={() => {
                            const last = getLastVisitedCatalogue();
                            if (last?.catalogueId === cid) {
                              const q = last.search ? `?${last.search}` : "";
                              history.push(`/catalogue-products/${encodeURIComponent(cid)}${q}`);
                              return;
                            }
                            history.push(`/catalogue-products/${encodeURIComponent(cid)}`);
                          }}
                        >
                          Open collection
                        </button>
                      ) : null}
                    </div>

                    {cart.length > 0 ? (
                      <div className="hkp-visitor-account-block">
                        <h3 className="hkp-visitor-account-h3">Cart</h3>
                        <div className="hkp-visitor-account-table-wrap">
                          <table className="hkp-visitor-invoice-table">
                            <thead>
                              <tr>
                                <th className="hkp-inv-col-thumb" scope="col">
                                  {" "}
                                </th>
                                <th className="hkp-inv-col-product" scope="col">
                                  Product
                                </th>
                                <th className="hkp-inv-col-unit" scope="col">
                                  Unit
                                </th>
                                <th className="hkp-inv-col-qty" scope="col">
                                  Qty
                                </th>
                                <th className="hkp-inv-col-amt" scope="col">
                                  Amount
                                </th>
                                <th className="hkp-inv-col-act" scope="col">
                                  {" "}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {cart.map((line) => {
                                const pk = line.productKey || "";
                                const qty = clampQty(Number(line.quantity) || 1);
                                const unit = parsePriceSnapshot(line.sellPriceMrpSnapshot);
                                const lineTotal = unit * qty;
                                const img = line.productImage?.trim();
                                const busyQty = mutating[`qty-${cid}-${pk}`];
                                const busyRm = mutating[`rmc-${cid}-${pk}`];
                                return (
                                  <tr key={pk || line.productUniqueId}>
                                    <td className="hkp-inv-col-thumb">
                                      {img ? (
                                        <img src={img} alt="" className="hkp-visitor-invoice-thumb" />
                                      ) : (
                                        <div className="hkp-visitor-invoice-thumb-ph" aria-hidden />
                                      )}
                                    </td>
                                    <td className="hkp-inv-col-product">
                                      <div className="hkp-inv-product-name">
                                        {line.productName || line.productUniqueId || "Product"}
                                      </div>
                                      {line.designNum ? (
                                        <div className="hkp-inv-product-meta">Design {line.designNum}</div>
                                      ) : null}
                                    </td>
                                    <td className="hkp-inv-col-unit">
                                      {unit > 0 ? formatInr(unit) : "—"}
                                    </td>
                                    <td className="hkp-inv-col-qty">
                                      <div className="hkp-visitor-account-qty">
                                        <button
                                          type="button"
                                          className="hkp-visitor-account-qty-btn"
                                          disabled={busyQty || busyRm || qty <= 1}
                                          aria-label="Decrease quantity"
                                          onClick={() => void onCartQty(cid, line, qty - 1)}
                                        >
                                          −
                                        </button>
                                        <span className="hkp-visitor-account-qty-val">{qty}</span>
                                        <button
                                          type="button"
                                          className="hkp-visitor-account-qty-btn"
                                          disabled={busyQty || busyRm || qty >= 999}
                                          aria-label="Increase quantity"
                                          onClick={() => void onCartQty(cid, line, qty + 1)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </td>
                                    <td className="hkp-inv-col-amt">
                                      {unit > 0 ? formatInr(lineTotal) : "—"}
                                    </td>
                                    <td className="hkp-inv-col-act">
                                      <button
                                        type="button"
                                        className="hkp-visitor-account-remove"
                                        disabled={busyRm || busyQty}
                                        onClick={() => void onRemoveCart(cid, pk)}
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    {shortlist.length > 0 ? (
                      <div className="hkp-visitor-account-block">
                        <h3 className="hkp-visitor-account-h3">Shortlist</h3>
                        <div className="hkp-visitor-account-table-wrap">
                          <table className="hkp-visitor-invoice-table">
                            <thead>
                              <tr>
                                <th className="hkp-inv-col-thumb" scope="col">
                                  {" "}
                                </th>
                                <th className="hkp-inv-col-product" scope="col">
                                  Product
                                </th>
                                <th className="hkp-inv-col-unit" scope="col">
                                  Note
                                </th>
                                <th className="hkp-inv-col-qty" scope="col">
                                  Qty
                                </th>
                                <th className="hkp-inv-col-amt" scope="col">
                                  Amount
                                </th>
                                <th className="hkp-inv-col-act" scope="col">
                                  {" "}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {shortlist.map((line) => {
                                const pk = line.productKey || "";
                                const img = line.productImage?.trim();
                                const busyRm = mutating[`rms-${cid}-${pk}`];
                                return (
                                  <tr key={pk || line.productUniqueId}>
                                    <td className="hkp-inv-col-thumb">
                                      {img ? (
                                        <img src={img} alt="" className="hkp-visitor-invoice-thumb" />
                                      ) : (
                                        <div className="hkp-visitor-invoice-thumb-ph" aria-hidden />
                                      )}
                                    </td>
                                    <td className="hkp-inv-col-product">
                                      <div className="hkp-inv-product-name">
                                        {line.productName || line.productUniqueId || "Product"}
                                      </div>
                                      {line.designNum ? (
                                        <div className="hkp-inv-product-meta">Design {line.designNum}</div>
                                      ) : null}
                                    </td>
                                    <td className="hkp-inv-col-unit">Saved for later</td>
                                    <td className="hkp-inv-col-qty">—</td>
                                    <td className="hkp-inv-col-amt">—</td>
                                    <td className="hkp-inv-col-act">
                                      <button
                                        type="button"
                                        className="hkp-visitor-account-remove"
                                        disabled={busyRm}
                                        onClick={() => void onRemoveShortlist(cid, pk)}
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    <div className="hkp-visitor-account-section-foot">
                      <span className="hkp-visitor-account-foot-label">Section subtotal</span>
                      <span className="hkp-visitor-account-foot-value">{formatInr(sectionSubtotal)}</span>
                      <span className="hkp-visitor-account-foot-hint">
                        {sectionPieces} piece{sectionPieces === 1 ? "" : "s"} in this collection
                      </span>
                    </div>

                    <div className="hkp-visitor-account-order-panel">
                      <details className="hkp-visitor-account-extra">
                        <summary>Add GST or address (optional)</summary>
                        <label htmlFor={`hkp-visitor-submit-gst-${cid}`}>GST number</label>
                        <input
                          id={`hkp-visitor-submit-gst-${cid}`}
                          type="text"
                          autoComplete="off"
                          value={submitGst}
                          onChange={(e) => setSubmitGst(e.target.value)}
                          placeholder="15-character GSTIN, if you buy as a business"
                        />
                        <label htmlFor={`hkp-visitor-submit-address-${cid}`}>Address</label>
                        <textarea
                          id={`hkp-visitor-submit-address-${cid}`}
                          rows={3}
                          value={submitAddress}
                          onChange={(e) => setSubmitAddress(e.target.value)}
                          placeholder="Delivery or billing address, if needed"
                        />
                      </details>
                      <p className="hkp-visitor-account-order-panel-label">Send to studio</p>
                      {cart.length > 0 && shortlist.length > 0 ? (
                        <button
                          type="button"
                          className="hkp-visitor-account-submit-btn"
                          disabled={submitBusy || anyMutating || !cid}
                          onClick={() => void onSubmitShelf(cid, "both")}
                        >
                          {submitBusy ? "Sending…" : "Submit cart & shortlist"}
                        </button>
                      ) : null}
                      {cart.length > 0 ? (
                        <button
                          type="button"
                          className={
                            shortlist.length > 0
                              ? "hkp-visitor-account-submit-btn hkp-visitor-account-submit-btn--outline-match"
                              : "hkp-visitor-account-submit-btn"
                          }
                          disabled={submitBusy || anyMutating || !cid}
                          onClick={() => void onSubmitShelf(cid, "cart")}
                        >
                          Submit cart only
                        </button>
                      ) : null}
                      {shortlist.length > 0 ? (
                        <button
                          type="button"
                          className="hkp-visitor-account-outline-btn hkp-visitor-account-outline-btn--order-wide"
                          disabled={submitBusy || anyMutating || !cid}
                          onClick={() => void onSubmitShelf(cid, "shortlist")}
                        >
                          Submit shortlist only
                        </button>
                      ) : null}
                    </div>
                  </section>
                );
              })}
                </div>
              </>
            )}

            {orderLog.length > 0 ? (
              <div className="mt-4 pt-3 border-top">
                <VisitorSubmittedOrdersSection
                  orderLog={orderLog}
                  expandedOrders={expandedOrders}
                  onToggleOrder={(id) =>
                    setExpandedOrders((prev) => ({ ...prev, [id]: !prev[id] }))
                  }
                  profile={
                    profile
                      ? { name: profile.name, phone: profile.phone, email: profile.email }
                      : null
                  }
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      {isVisitorSession && !loading && !error && nonEmptyShelves.length > 0 ? (
        <footer className="hkp-visitor-account-sticky-foot" role="region" aria-label="Order summary">
          <div className="hkp-visitor-account-sticky-inner">
            <div className="hkp-visitor-account-sticky-summary">
              <div className="hkp-visitor-account-foot-strong">Subtotal (priced lines)</div>
              <div className="hkp-visitor-account-foot-muted">
                {totals.lineCount} line{totals.lineCount === 1 ? "" : "s"} · {totals.pieceCount} piece
                {totals.pieceCount === 1 ? "" : "s"} · Final send uses your sign-in name
              </div>
              <div className="hkp-visitor-account-foot-total">{formatInr(totals.subtotal)}</div>
            </div>
            <div className="hkp-visitor-account-sticky-cta">
              <p className="hkp-visitor-account-submit-hint mb-0">
                Use the <strong>Submit</strong> buttons in each collection to send your cart or shortlist.
                Submitted requests appear at the bottom of this page. Each send uses your registered name
                and phone.
              </p>
            </div>
          </div>
        </footer>
      ) : null}
    </SharedCatalogueChrome>
  );
};

/** Empty and signed-out states: say what this page is for and give one next step. */
function EmptyCard({
  title,
  body,
  steps,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  steps?: string[];
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <section className="hkp-visitor-empty" aria-label={title}>
      <div className="hkp-visitor-empty-mark" aria-hidden="true">
        ♡
      </div>
      <h1>{title}</h1>
      <p>{body}</p>
      {steps?.length ? (
        <ol>
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      ) : null}
      {actionLabel && onAction ? (
        <button type="button" className="hkp-visitor-empty-btn" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </section>
  );
}

const PublicCatalogueVisitorAccount: React.FC = () => (
  <CatalogueVisitorProvider>
    <PublicCatalogueVisitorAccountInner />
  </CatalogueVisitorProvider>
);

export default PublicCatalogueVisitorAccount;
